import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
  Step,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const CalendarAndEvents = () => (
  <Page
    title="Event Calendar and Creating Events"
    intro="The calendar is the main entry point for event work. It shows every event colour-coded by status, with an at-a-glance indicator of whether its shifts are covered, and links through to each event's record."
  >
    <Section title="Finding your way around the calendar">
      <Bullets>
        <Bullet>
          Open <DocLink url="/events/calendar" text="Event Calendar" /> from
          the sidebar under <strong>Events</strong>. Use the arrow buttons to
          move between periods and <strong>Today</strong> to jump back to the
          current date.
        </Bullet>
        <Bullet>
          A segmented control switches between <strong>Month</strong> (the
          default on desktop), <strong>Week</strong>, <strong>Day</strong>,
          and <strong>List</strong> views. Weeks start on Monday, and on a
          phone the calendar opens in List view instead of the month grid.
        </Bullet>
        <Bullet>
          The header caption summarises what's in view — for example{' '}
          <em>12 events · 3 with shift gaps</em> — so uncovered events stand
          out before you scroll.
        </Bullet>
        <Bullet>
          Each event is coloured by its status (Potential, Interested, Quote
          Sent, Planning, Ready, Event Finished, and so on). Your organisation
          can customise the colours in Site Configuration.
        </Bullet>
        <Bullet>
          A small badge to the left of each event title shows shift cover: a
          grey circle means <strong>No shifts yet</strong>, a green person
          means <strong>All shifts filled</strong>, and a red person means{' '}
          <strong>Shifts uncovered</strong>. Hover the badge for the label.
        </Bullet>
        <Bullet>
          The footer below the calendar is a legend: a coloured dot for each
          event status currently in view, followed by the three shift-cover
          icons (<strong>Shifts covered</strong>, <strong>Shift gaps</strong>,{' '}
          <strong>No shifts yet</strong>).
        </Bullet>
        <Bullet>
          Cancelled events are hidden by default. The <strong>eye icon</strong>{' '}
          next to the view control shows or hides them — it turns green while
          cancelled events are visible.
        </Bullet>
        <Bullet>
          Click an event to open it. Planners go to the full event record;
          staff outside the Event Planning team are taken to the read-only
          event information view instead.
        </Bullet>
      </Bullets>
      <Screenshot url="events/event-calendar.png">
        The Event Calendar in Month view with status-coloured events and their
        shift-cover badges.
      </Screenshot>
      <Info title="Who can do this?">
        The calendar is visible to any signed-in user. The{' '}
        <strong>Add Event</strong> button and the full event record need the{' '}
        <strong>Event Planning</strong> team on your staff profile (site
        administrators always have access).
      </Info>
    </Section>

    <Section title="Creating an event">
      <Step number="1" title="Start a new event">
        Click <strong>Add Event</strong> on the calendar, or go straight to{' '}
        <DocLink url="/events/add-new" text="Add New Event" />. If the date
        range you're viewing has no events yet, the calendar also offers a{' '}
        <strong>Create an Event</strong> prompt that takes you to the same
        form.
      </Step>
      <Step number="2" title="Enter the essentials">
        Give the event an <strong>Event Title</strong> and set the{' '}
        <strong>Start Date/Time</strong> and <strong>End Date/Time</strong> —
        these three are required. Pick an <strong>Event Type</strong> from the
        list (Live Music, Equestrian XC, Motor Sports, and so on).
      </Step>
      <Step number="3" title="Choose the client organisation">
        Select the <strong>Organisation</strong> the event is for. If the
        client doesn't exist yet, use the green plus icon (or the{' '}
        <strong>Add New</strong> button shown when no match is found) to
        create one without leaving the form.
      </Step>
      <Step number="4" title="Add the venue address">
        Under <strong>Event Location</strong>, use{' '}
        <strong>Find address or postcode</strong> to look the venue up, then
        tidy the address lines (line one and two, town, county, post code) by
        hand if needed. Picking an address from the lookup also sets the map
        point used to centre the event's control room map — typing the lines
        manually keeps any point already looked up.{' '}
        <strong>What3Words</strong> records the precise spot. Everything here
        is optional and can be refined later by editing the event. Staff on
        Available Shifts see a short location derived from the address (town
        and postcode).
      </Step>
      <Step number="5" title="Check for clashes and submit">
        The <strong>Conflicting Events</strong> panel lists any other events
        overlapping your chosen dates — useful for spotting resourcing
        clashes. Click <strong>Submit</strong> and you land straight in the
        new event's record, ready to plan.
      </Step>
      <Info title="Deleting an event">
        A red <strong>Delete Event</strong> button appears when editing the
        event's details, but only while no shifts are assigned and there are
        no shift applications. After that point, cancel the event instead
        (from the status chip on the event record) so the history is kept.
      </Info>
    </Section>
  </Page>
)

export default CalendarAndEvents
